import { z } from "zod";
import { AppCodeSchema, type Audience, type Operator, i18nTextSchema } from "./schema";

/**
 * Attribute catalog — spec §Attribute catalog. One per consumer app: it drives the editor
 * dropdowns, rejects bad conditions at save, and types the app's context.
 */

export const ATTRIBUTE_TYPES = ["string", "enum", "list", "number"] as const;
export type AttributeType = (typeof ATTRIBUTE_TYPES)[number];

/** Spec §Audience, "Allowed operators". `exists` / `notExists` fit every type. */
export const OPERATORS_BY_TYPE: Record<AttributeType, readonly Operator[]> = {
  string: ["is", "isNot", "isOneOf", "contains", "matches", "exists", "notExists"],
  enum: ["is", "isNot", "isOneOf", "exists", "notExists"],
  list: ["contains", "notContains", "exists", "notExists"],
  number: ["eq", "gt", "lt", "between", "exists", "notExists"],
};

export const AttributeDefSchema = z
  .object({
    key: z.string().regex(/^[a-z][A-Za-z0-9]*(\.[a-z][A-Za-z0-9]*)+$/, "Dotted key, e.g. `tenant.id`"),
    type: z.enum(ATTRIBUTE_TYPES),
    values: z.array(z.string().min(1)).min(1).readonly().optional(),
    description: i18nTextSchema(200),
    source: z.string().min(1),
  })
  .superRefine((a, ctx) => {
    if (a.type === "enum" && !a.values)
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["values"], message: "An enum attribute requires values" });
    if (a.type !== "enum" && a.values)
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["values"], message: "Only an enum attribute has values" });
  });
export type AttributeDef = z.infer<typeof AttributeDefSchema>;

export type Context = Record<string, unknown>;

/** True when `value` is a well-typed context value for `def`. */
export function contextValueFits(def: AttributeDef, value: unknown): boolean {
  switch (def.type) {
    case "string":
      return typeof value === "string";
    case "enum":
      return typeof value === "string" && (def.values ?? []).includes(value);
    case "list":
      return Array.isArray(value) && value.every((v) => typeof v === "string");
    case "number":
      return typeof value === "number" && Number.isFinite(value);
  }
}

export const SampleContextSchema = z.object({
  label: z.string().min(1),
  context: z.record(z.unknown()),
});
export type SampleContext = z.infer<typeof SampleContextSchema>;

export const AttributeCatalogSchema = z
  .object({
    appCode: AppCodeSchema,
    attributes: z.array(AttributeDefSchema).min(1).readonly(),
    samples: z.array(SampleContextSchema).readonly(),
  })
  .superRefine((cat, ctx) => {
    const byKey = new Map<string, AttributeDef>();
    cat.attributes.forEach((a, i) => {
      if (byKey.has(a.key))
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["attributes", i, "key"], message: `Duplicate key ${a.key}` });
      byKey.set(a.key, a);
    });
    cat.samples.forEach((s, i) => {
      for (const [key, value] of Object.entries(s.context)) {
        const def = byKey.get(key);
        if (!def)
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["samples", i, "context", key], message: "Not in catalog" });
        else if (value !== undefined && !contextValueFits(def, value))
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["samples", i, "context", key],
            message: `Not a valid ${def.type}`,
          });
      }
    });
  });
export type AttributeCatalog = z.infer<typeof AttributeCatalogSchema>;

export function findAttribute(catalog: AttributeCatalog, key: string): AttributeDef | undefined {
  return catalog.attributes.find((a) => a.key === key);
}

/**
 * Save-time check of spec §Audience: a condition on an attribute outside the catalog, or with
 * an operator its type does not allow, is rejected (API 400). Returns one message per bad
 * condition; empty means valid.
 */
export function audienceCatalogIssues(audience: Audience, catalog: AttributeCatalog): string[] {
  if (audience.mode === "everyone") return [];
  const issues: string[] = [];
  audience.groups.forEach((g, gi) =>
    g.conditions.forEach((c, ci) => {
      const def = findAttribute(catalog, c.attribute);
      const at = `groups[${gi}].conditions[${ci}]`;
      if (!def) issues.push(`${at}: attribute \`${c.attribute}\` is not in the ${catalog.appCode} catalog`);
      else if (!OPERATORS_BY_TYPE[def.type].includes(c.op))
        issues.push(`${at}: operator \`${c.op}\` does not apply to ${def.type} attribute \`${c.attribute}\``);
    }),
  );
  return issues;
}
